import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { uniqueId } from '../form/ids';

export interface SettingsNavItem {
  readonly label: string;
  readonly link: string;
  /** Mancano dati necessari: compare il punto giallo con il testo nascosto "da completare". */
  readonly attention?: boolean;
  readonly testId?: string;
}

export interface SettingsNavGroup {
  readonly label: string;
  readonly items: readonly SettingsNavItem[];
}

/**
 * Menu secondario verticale delle impostazioni su desktop (per esempio "Profilo e sicurezza").
 * La larghezza la decide il genitore. In testa si può proiettare un'intestazione (avatar e nome).
 * La voce attiva ha `bg-stone-100 font-semibold` e `aria-current="page"`.
 */
@Component({
  selector: 'bs-settings-nav',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, RouterLinkActive],
  host: { class: 'block' },
  template: `
    <nav [attr.aria-label]="label()" [attr.data-testid]="testId()" class="flex flex-col gap-4">
      <ng-content />
      @for (group of groups(); track group.label; let index = $index) {
        <div class="flex flex-col gap-1">
          <h2
            [id]="groupId(index)"
            class="px-3 pb-1 text-xs font-semibold text-stone-500 dark:text-stone-400"
          >
            {{ group.label }}
          </h2>
          <ul class="flex flex-col gap-0.5" [attr.aria-labelledby]="groupId(index)">
            @for (item of group.items; track item.link) {
              <li>
                <a
                  [routerLink]="item.link"
                  routerLinkActive
                  #active="routerLinkActive"
                  ariaCurrentWhenActive="page"
                  [class]="active.isActive ? activeClass : idleClass"
                  [attr.data-testid]="item.testId ?? null"
                  [attr.data-active]="active.isActive ? 'true' : null"
                >
                  <span class="min-w-0 flex-1 truncate">{{ item.label }}</span>
                  @if (item.attention) {
                    <span
                      class="size-2 shrink-0 rounded-full bg-bsite-400"
                      aria-hidden="true"
                    ></span>
                    <span class="sr-only">, da completare</span>
                  }
                </a>
              </li>
            }
          </ul>
        </div>
      }
    </nav>
  `,
})
export class SettingsNavComponent {
  readonly groups = input.required<readonly SettingsNavGroup[]>();
  /** Nome accessibile del menu. */
  readonly label = input('Sezioni delle impostazioni');
  readonly testId = input<string | null>(null);

  private readonly id = uniqueId('bs-settings-nav');
  private readonly base =
    'flex h-10 items-center gap-2 rounded-xl px-3 text-sm transition-colors pointer-coarse:h-11';
  protected readonly activeClass = `${this.base} bg-stone-100 font-semibold text-ink dark:bg-stone-800 dark:text-stone-50`;
  protected readonly idleClass = `${this.base} font-medium text-stone-600 hover:bg-stone-100 hover:text-ink dark:text-stone-300 dark:hover:bg-stone-800 dark:hover:text-stone-50`;

  protected groupId(index: number): string {
    return `${this.id}-group-${index}`;
  }
}
